import React, { useId, useState, type ReactNode } from 'react';

/**
 * `ProgressiveDisclosureSection` — accessible expand/collapse container built
 * on the native `<details>`/`<summary>` primitives. Used by the public
 * developer docs to keep the first viewport short and reveal deeper topics on
 * demand.
 *
 * Why a custom wrapper instead of headless-ui/radix?
 *   - Native `<details>` is keyboard accessible by default and works without
 *     JavaScript.
 *   - The codebase disallows new visual dependencies for this feature.
 */
export interface ProgressiveDisclosureSectionProps {
  /** Title rendered inside the always-visible summary. */
  title: string;
  /** Optional short caption under the title. */
  description?: string;
  /** Body content revealed when expanded. */
  children: ReactNode;
  /** Opens the section on first render (uncontrolled). */
  defaultOpen?: boolean;
  /** Controlled open state; when set, parent owns expand/collapse. */
  open?: boolean;
  /** DOM id for anchor linking; auto-generated when omitted. */
  id?: string;
  /** Optional test hook. */
  'data-testid'?: string;
  /** Extra classes appended to the outer `<details>`. */
  className?: string;
  /** Callback fired when the section is opened or closed. */
  onToggle?: (isOpen: boolean) => void;
}

export const ProgressiveDisclosureSection: React.FC<ProgressiveDisclosureSectionProps> = ({
  title,
  description,
  children,
  defaultOpen = false,
  open,
  id,
  'data-testid': dataTestId,
  className = '',
  onToggle,
}) => {
  const reactId = useId();
  const headingId = id ?? `progressive-disclosure-${reactId.replace(/:/g, '')}`;
  const contentId = `${headingId}-content`;
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = isControlled ? open : internalOpen;

  const handleSummaryClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const nextOpen = !isOpen;
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    onToggle?.(nextOpen);
  };

  return (
    <details
      id={headingId}
      open={isOpen}
      data-testid={dataTestId}
      className={`group border-b border-outline-default/40 transition-colors ${className}`}
    >
      <summary
        onClick={handleSummaryClick}
        className="flex cursor-pointer list-none items-center justify-between gap-3 py-3 text-left text-glyph-primary transition-colors duration-fast ease-smooth hover:text-accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary"
        aria-controls={contentId}
        aria-expanded={isOpen}
      >
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3 className="m-0 text-sm leading-snug font-semibold text-glyph-primary">{title}</h3>
          {description ? (
            <p className="m-0 text-xs leading-snug text-glyph-tertiary">{description}</p>
          ) : null}
        </div>
        <span
          aria-hidden
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-glyph-secondary transition-transform duration-fast ease-smooth group-open:rotate-180"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.5 4.5L6 8L9.5 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </summary>
      <div
        id={contentId}
        className="flex flex-col gap-3 pt-0 pb-4 text-sm leading-snug text-glyph-primary"
      >
        {children}
      </div>
    </details>
  );
};
